import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import classnames from 'classnames';
import { trackEvent } from 'latitude-analytics';
import {
  BREAKPOINT,
  ANALYTICS,
  REBRAND_COLOR
} from '@latitude/core/utils/constants';
import { Link } from '@latitude/link';
import SvgInline from '@latitude/svg-inline';
import {
  AnalyticsContext,
  AnalyticsLocationProvider
} from 'latitude-analytics';
import Section from '@latitude/section-rebrand';
import Typography from '@latitude/typography';
import Button from '@latitude/button-rebrand';

/* ===========================
   Styled Components
=========================== */

const BrokerHeaderWrapper = styled.header`
  height: 80px;
  background-color: ${REBRAND_COLOR.WHITE};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 10000;

  @media (max-width: ${BREAKPOINT.LG}) {
    height: 56px;
  }
`;

const StyledSection = styled(Section)`
  height: 100%;
`;

const BrokerHeaderInnerWrapper = styled.div`
  position: relative;
  height: 100%;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 2;

  &:after {
    content: '';
    background-color: ${REBRAND_COLOR.WHITE};
    display: block;
    position: fixed;
    width: 100%;
    height: 80px;
    z-index: -2;
    top: 0;
    left: 0;
    pointer-events: none;

    @media (max-width: ${BREAKPOINT.LG}) {
      height: 56px;
    }
  }
`;

const BrandButtonSvg = styled(SvgInline)`
  width: 100%;

  path {
    fill: ${REBRAND_COLOR.WHITE};
  }
`;

const BrandButtonWrapper = styled(Link)`
  display: flex;
  align-items: center;
  width: 122px;
  margin-left: 20px;
  margin-right: 6px;

  @media (min-width: ${BREAKPOINT.SM}) {
    width: 170px;
  }

  @media (min-width: ${BREAKPOINT.LG}) {
    width: 202px;
  }
`;

const CustomHeaderNav = styled.nav`
  display: none;
  flex: 1;
  justify-content: center;
  align-items: center;
  gap: 24px;
  min-width: 0;
  padding: 0 16px;

  @media (min-width: ${BREAKPOINT.LG}) {
    display: flex;
  }
`;

const CustomHeaderNavLink = styled(Link)`
  color: ${REBRAND_COLOR.INDIGO};
  font-family: 'Montserrat', 'Roboto', sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  text-decoration: none !important;
  border-bottom: none !important;
  white-space: nowrap;
  transition: color 0.2s ease-in-out;

  &:hover,
  &:focus,
  &:active {
    text-decoration: none !important;
    border-bottom: none !important;
  }
`;

const HeaderActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;

  @media (max-width: ${BREAKPOINT.LG}) {
    gap: 10px;
  }
`;

const HeaderPhoneLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${REBRAND_COLOR.INDIGO};
  text-decoration: none !important;
  border-bottom: none !important;
`;

const HeaderPhoneLabel = styled.span`
  display: none;
  margin-left: 8px;
  font-family: 'Montserrat', 'Roboto', sans-serif;
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;

  @media (min-width: ${BREAKPOINT.LG}) {
    display: inline-flex;
  }
`;

const HeaderPhoneIcon = styled(SvgInline)`
  width: 20px;
  height: 20px;
`;

const BrokerLoginButtonWrapper = styled.div`
  width: 160px;
  height: 100%;

  @media (max-width: ${BREAKPOINT.LG}) {
    width: 155px;
    display: flex;
    justify-content: center;
    align-items: center;

    .is-active {
      background-color: rgba(255, 255, 255, 0.3);
    }
  }
`;

const LoginButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 18px;
  height: 100%;

  &:focus {
    outline: 0;
  }

  @media (max-width: ${BREAKPOINT.LG}) {
    font-size: 16px;
  }
`;

const LoginButtonSvg = styled(SvgInline)`
  path {
    fill: ${REBRAND_COLOR.MID_BLUE};
  }
  @media (max-width: ${BREAKPOINT.LG}) {
    display: none;
  }
`;

export const BrokerLoginModalWrapper = styled.div`
  width: 100vw !important;
  z-index: -5;
  position: absolute;
  top: 56px;
  padding: 15px;
  right: calc(-100% - 72px);
  width: 100%;
  background-color: white;
  transform: translateX(0);
  transition: transform 0.35s cubic-bezier(0.31, 0.005, 0.735, 0.01);
  box-shadow: none;
  border: 1px solid #f3f3f3;

  &.is-open {
    transform: translateX(-100%);
    transition: transform 0.5s cubic-bezier(0.235, 0.79, 0.65, 1);
    box-shadow: 0 0 0 2038px rgba(0, 0, 0, 0.5);
  }

  @media (min-width: ${BREAKPOINT.MD}) {
    right: -390px;
    top: 56px;
    transform: translateX(0);
    transition: transform 0.35s cubic-bezier(0.31, 0.005, 0.735, 0.01);
    width: 390px !important;
    box-shadow: none;

    &.is-open {
      transform: translateX(-100%);
      transition: transform 0.5s cubic-bezier(0.235, 0.79, 0.65, 1);
      box-shadow: 0 0 0 2038px rgba(0, 0, 0, 0.5);
    }
  }

  @media (min-width: ${BREAKPOINT.LG}) {
    position: absolute;
    right: 11px;
    top: 0px;
    transform: translateY(-100%);
    transition: transform 0.35s cubic-bezier(0.31, 0.005, 0.735, 0.01);

    &.is-open {
      transform: translateY(80px);
      transition: transform 0.5s cubic-bezier(0.235, 0.79, 0.65, 1);
      box-shadow: 0 4px 6px -2px rgb(0 0 0 / 25%);
    }
  }

  > * {
    &:not(:last-child) {
      margin-bottom: 15px;
    }
  }
`;

const ModalItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  background-color: ${REBRAND_COLOR.BLACK_20};
  padding: 24px;
  a {
    width: fit-content;
    border-color: ${REBRAND_COLOR.MID_BLUE};
    &:hover {
      border-color: ${REBRAND_COLOR.MID_BLUE};
    }
  }
`;

/* ===========================
   Components
=========================== */

const BrandButton = () => (
     <Link
        noStyle
        href="/"
        className={classnames('home-button-ltd trackcustomanalytics')}
        trackId="nav-home"
        trackEventData={{
          label: 'Latitude'
        }}
      >
         <SvgInline
          name="latitude-rebrand-logo"
          aria-label="Home page"
          className="home-button__logo"
        />
      </Link>
);

const BrokerLoginButton = ({ isModalActive, setIsModalActive, analytics }) => {
  const handleOnClick = () => {
    if (trackEvent) {
      trackEvent(analytics, {
        category: 'internal-link',
        action: 'login',
        label: 'broker login',
        location: 'header'
      });
    }

    setIsModalActive(!isModalActive);
  };

  return (
    <BrokerLoginButtonWrapper
      className={classnames(isModalActive && 'is-active')}
    >
      <LoginButton
        onClick={handleOnClick}
        type="button"
        aria-label="Broker login"
      >
        <Typography.CTARegular>Broker Log in</Typography.CTARegular>
        <LoginButtonSvg
          aria-label="Down"
          name={isModalActive ? 'chevron-up' : 'chevron-down'}
        />
      </LoginButton>
    </BrokerLoginButtonWrapper>
  );
};

const BrokerLoginModal = ({ isModalActive, modalItems }) => (
  <BrokerLoginModalWrapper
    className={classnames({
      'is-open': isModalActive
    })}
  >
    {modalItems?.map(({ heading, description, button, button2 }, index) => (
      <ModalItem key={index}>
        <Typography.CTARegular color={REBRAND_COLOR.INDIGO}>
          {heading}
        </Typography.CTARegular>
        <Typography.Body color={REBRAND_COLOR.BLACK_80}>
          {description}
        </Typography.Body>

        <Typography.LinkRegular
          color={REBRAND_COLOR.MID_BLUE}
          aria-label={button.label}
          href={button.href}
          trackEventData={{
            action: button.action,
            category: button.category
          }}
          target={button.target}
        >
          {button.label}
        </Typography.LinkRegular>

        {heading === 'Submit an Asset Loan application' && button2 && (
          <>
            <br />
            <br />
            <Typography.LinkRegular
              color={REBRAND_COLOR.MID_BLUE}
              aria-label={button2.label}
              href={button2.href}
              trackEventData={{
                action: button2.action,
                category: button2.category
              }}
              target={button2.target}
            >
              {button2.label}
            </Typography.LinkRegular>
          </>
        )}
      </ModalItem>
    ))}
  </BrokerLoginModalWrapper>
);

/* ===========================
   Main Component
=========================== */

const BrokerHeader = ({ headerData, customLayout = null }) => {
  const [isModalActive, setIsModalActive] = useState(false);
  const wrapperRef = useRef(null);
  const isCustomLayout = !!customLayout;
  const navItems = customLayout?.navItems ?? [];
  const BrokerApplyButton = () => (
  <BrokerLoginButtonWrapper>
    <Button
      variant={customLayout?.button?.variant}
      size={customLayout?.button?.size}
      ctaText={customLayout?.button?.text}
      ctaUrl={customLayout?.button?.href}
      trackEventData={customLayout?.button?.trackEventData}
      trackId={customLayout?.button?.trackId}
    />
  </BrokerLoginButtonWrapper>
);

  useEffect(() => {
    const handleClickOutside = event => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsModalActive(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <AnalyticsLocationProvider location={ANALYTICS.LOCATION.HEADER}>
      <AnalyticsContext.Consumer>
        {([analytics]) => (
          <BrokerHeaderWrapper data-sticky-navigation-offset>
            <StyledSection>
              <BrokerHeaderInnerWrapper ref={wrapperRef}>
                <BrandButton site={headerData.site} />

                {isCustomLayout ? (
                  <>
                    <CustomHeaderNav aria-label="Broker page navigation">
                      {navItems.map(({ label, href }) => (
                        <CustomHeaderNavLink href={href} key={href}>
                          {label}
                        </CustomHeaderNavLink>
                      ))}
                    </CustomHeaderNav>

                    <HeaderActionsWrapper>
                      <HeaderPhoneLink
                        href={customLayout?.phone?.href}
                        aria-label={customLayout?.phone?.ariaLabel}
                      >
                        <HeaderPhoneIcon
                          aria-label={customLayout?.phone?.iconAriaLabel}
                          name={customLayout?.phone?.icon}
                        />
                        <HeaderPhoneLabel>{customLayout?.phone?.text}</HeaderPhoneLabel>
                      </HeaderPhoneLink>

                      <BrokerApplyButton />
                    </HeaderActionsWrapper>
                  </>
                ) : (
                  <BrokerLoginButton
                    isModalActive={isModalActive}
                    setIsModalActive={setIsModalActive}
                    analytics={analytics}
                  />
                )}

                <BrokerLoginModal
                  isModalActive={isModalActive}
                  modalItems={headerData.modalItems}
                />
              </BrokerHeaderInnerWrapper>
            </StyledSection>
          </BrokerHeaderWrapper>
        )}
      </AnalyticsContext.Consumer>
    </AnalyticsLocationProvider>
  );
};

export default BrokerHeader;
